import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Clock, Wrench, BookOpen, Users as UsersIcon, Sparkles, MoreHorizontal, Pencil, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import DateField from '../components/DateField';
import { MaintenanceScheduleLabel } from '../components/maintenance/MaintenanceScheduleLabel';
import { scheduleCycleLabel, scheduleOptionLabel } from '../utils/maintenanceSchedules';
import './ShopTime.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  general: { label: 'General', icon: Clock },
  equipment_maintenance: { label: 'Equipment Maintenance', icon: Wrench },
  training: { label: 'Training', icon: BookOpen },
  meeting: { label: 'Meeting', icon: UsersIcon },
  cleaning: { label: 'Cleaning', icon: Sparkles },
  other: { label: 'Other', icon: MoreHorizontal },
};

export const prettyCategory = (cat) =>
  CATEGORY_META[cat]?.label || cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const categoryIcon = (cat) => CATEGORY_META[cat]?.icon || Clock;

export default function ShopTime() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    tech_id: '',
    date: new Date().toISOString().slice(0, 10),
    hours: '',
    overtime_hours: '',
    category: 'general',
    equipment_id: '',
    schedule_id: '',
    description: '',
  });
  const autoFillRef = useRef('');

  const canManageOthers = ['management', 'foreman'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    try {
      const [eRes, tRes, eqRes, catRes] = await Promise.all([
        axios.get(`${API}/shop-time`),
        axios.get(`${API}/technicians`),
        axios.get(`${API}/equipment`),
        axios.get(`${API}/shop-time/categories`),
      ]);
      setEntries(eRes.data);
      setTechnicians(tRes.data);
      setEquipment(eqRes.data);
      setCategories(catRes.data.categories || []);
    } catch {
      toast.error('Failed to load shop time data');
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // For technicians, pre-select themselves
    if (user?.role === 'technician') {
      // resolve after technicians load
    }
  }, [fetchAll, user]);

  useEffect(() => {
    if (user?.role === 'technician' && technicians.length && !form.tech_id) {
      const me = technicians.find(
        (t) => (t.name || '').toLowerCase().trim() === (user.name || '').toLowerCase().trim()
      );
      if (me) setForm((f) => ({ ...f, tech_id: me.id }));
    }
  }, [technicians, user, form.tech_id]);

  const resetForm = () => {
    setForm({
      tech_id: user?.role === 'technician'
        ? technicians.find((t) => (t.name || '').toLowerCase().trim() === (user.name || '').toLowerCase().trim())?.id || ''
        : '',
      date: new Date().toISOString().slice(0, 10),
      hours: '',
      overtime_hours: '',
      category: 'general',
      equipment_id: '',
      schedule_id: '',
      description: '',
    });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tech_id || !form.hours || !form.category) {
      toast.error('Tech, hours and category are required');
      return;
    }
    if (form.category === 'equipment_maintenance' && !form.equipment_id) {
      toast.error('Select equipment for Equipment Maintenance');
      return;
    }
    try {
      const payload = {
        tech_id: form.tech_id,
        date: form.date,
        hours: parseFloat(form.hours),
        overtime_hours: form.overtime_hours ? parseFloat(form.overtime_hours) : null,
        category: form.category,
        equipment_id: form.category === 'equipment_maintenance' ? form.equipment_id : null,
        schedule_id: form.category === 'equipment_maintenance' && form.schedule_id ? form.schedule_id : null,
        description: form.description.trim() || null,
      };
      if (editing) {
        await axios.patch(`${API}/shop-time/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/shop-time`, payload);
        toast.success('Logged');
      }
      resetForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (entry) => {
    setEditing(entry);
    setForm({
      tech_id: entry.tech_id,
      date: entry.date,
      hours: String(entry.hours),
      overtime_hours: entry.overtime_hours ? String(entry.overtime_hours) : '',
      category: entry.category,
      equipment_id: entry.equipment_id || '',
      schedule_id: entry.schedule_id || '',
      description: entry.description || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shop time entry?')) return;
    try {
      await axios.delete(`${API}/shop-time/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="shop-time-page">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">Shop Time</h3>
          <p className="text-slate-600 text-sm">
            Log non-job time — general shop work, training, equipment maintenance, and more.
          </p>
        </div>

        <Card className="p-6 bg-white border border-slate-200 shadow-sm">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            {editing ? <Pencil size={18} /> : <Plus size={18} />}
            {editing ? 'Edit Shop Time' : 'Log Shop Time'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="shop-time-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Technician</Label>
                <Select
                  value={form.tech_id}
                  onValueChange={(v) => setForm({ ...form, tech_id: v })}
                  disabled={user?.role === 'technician'}
                >
                  <SelectTrigger data-testid="shop-tech-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {technicians
                      .filter((t) =>
                        canManageOthers ||
                        (t.name || '').toLowerCase().trim() === (user?.name || '').toLowerCase().trim()
                      )
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Date</Label>
                <DateField
                  value={form.date}
                  onChange={(v) => setForm({ ...form, date: v })}
                  data-testid="shop-date"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="shop-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{prettyCategory(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.category === 'equipment_maintenance' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <Label className="text-xs text-slate-500">Equipment</Label>
                  <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v, schedule_id: '' })}>
                    <SelectTrigger data-testid="shop-equipment-select"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                    <SelectContent>
                      {equipment.map((e) => (
                        <SelectItem key={e.id} value={e.id} data-testid={`shop-equipment-option-${e.id}`}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const eq = equipment.find((e) => e.id === form.equipment_id);
                  const scheds = eq?.maintenance_schedules || [];
                  if (!eq || scheds.length === 0) return null;
                  const selectedSchedule = scheds.find((s) => s.id === form.schedule_id);
                  return (
                    <div className="min-w-0">
                      <Label className="text-xs text-slate-500">Complete schedule (optional)</Label>
                      <Select value={form.schedule_id || '__none'} onValueChange={(v) => {
                        const sid = v === '__none' ? '' : v;
                        setForm((prev) => {
                          const next = { ...prev, schedule_id: sid };
                          const s = scheds.find((x) => x.id === sid);
                          if (s) {
                            const label = scheduleCycleLabel(s);
                            const autoText = s.notes ? `${label} — ${s.notes}` : label;
                            if (!prev.description.trim() || prev.description === autoFillRef.current) {
                              next.description = autoText;
                              autoFillRef.current = autoText;
                            }
                          } else if (prev.description === autoFillRef.current) {
                            next.description = '';
                            autoFillRef.current = '';
                          }
                          return next;
                        });
                      }}>
                        <SelectTrigger className="h-auto min-h-10 gap-2 whitespace-normal [&>span]:line-clamp-none [&>span]:min-w-0 [&>span]:flex-1 [&>svg]:shrink-0" data-testid="shop-schedule-select">
                          <SelectValue>{selectedSchedule ? <MaintenanceScheduleLabel schedule={selectedSchedule} testPrefix="shop-schedule-selected" selected /> : '— None (log hours only) —'}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]" data-testid="shop-schedule-options">
                          <SelectItem value="__none" data-testid="shop-schedule-none">— None (log hours only) —</SelectItem>
                          {scheds.map((s) => (
                            <SelectItem key={s.id} value={s.id} textValue={scheduleOptionLabel(s)} className="min-w-0 [&>span:last-child]:min-w-0 [&>span:last-child]:w-full" data-testid={`shop-schedule-option-${s.id}`}>
                              <MaintenanceScheduleLabel schedule={s} testPrefix={`shop-schedule-option-${s.id}`} />
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Linking rolls this schedule&apos;s Last Done / Next Due forward automatically.
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Hours</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  placeholder="e.g. 2.5"
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  data-testid="shop-hours"
                  required
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Overtime Hours</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  placeholder="optional"
                  value={form.overtime_hours}
                  onChange={(e) => setForm({ ...form, overtime_hours: e.target.value })}
                  data-testid="shop-overtime"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Description (optional)</Label>
              <Textarea
                rows={2}
                placeholder="What was worked on?"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="shop-description"
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="bg-slate-900" data-testid="shop-submit-btn">
                {editing ? 'Save Changes' : 'Log Shop Time'}
              </Button>
              {editing && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* History */}
        <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="shop-time-history">
          <h4 className="text-lg font-bold text-slate-900 mb-4">Recent Shop Time</h4>
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No shop time logged yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full shop-time-history-table" data-testid="shop-time-history-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Date', 'Tech', 'Category', 'Equipment', 'Hours', 'OT', 'Description', ''].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {entries.map((e) => {
                    const Icon = categoryIcon(e.category);
                    return (
                      <tr key={e.id} className="hover:bg-slate-50" data-testid={`shop-row-${e.id}`}>
                        <td data-label="Date" className="px-3 py-2 text-sm text-slate-900">{e.date}</td>
                        <td data-label="Technician" className="px-3 py-2 text-sm text-slate-900">{e.tech_name}</td>
                        <td data-label="Category" className="px-3 py-2 text-sm text-slate-700">
                          <span className="flex items-center gap-1"><Icon size={14} className="text-slate-500 shrink-0" />{prettyCategory(e.category)}</span>
                        </td>
                        <td data-label="Equipment" className="px-3 py-2 text-sm text-slate-600">{e.equipment_name || '-'}</td>
                        <td data-label="Hours" className="px-3 py-2 text-sm font-mono text-slate-900">{e.hours}</td>
                        <td data-label="Overtime" className={`px-3 py-2 text-sm font-mono ${e.overtime_hours > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                          {e.overtime_hours > 0 ? `+${e.overtime_hours}` : '-'}
                        </td>
                        <td data-label="Description" className="px-3 py-2 text-sm text-slate-600 max-w-[200px] truncate">{e.description || '-'}</td>
                        <td data-label="Actions" className="px-3 py-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(e)} data-testid={`edit-shop-${e.id}`}>
                              <Pencil size={12} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(e.id)} className="text-red-600" data-testid={`delete-shop-${e.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
